// src/components/modals/AuditExportModal.jsx
import React, { useState } from 'react';
import { X, Download, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { apiCall } from '../../utils/api';

// Default the date range to the last 30 days. The backend requires both
// from and to explicitly; this is just a sensible UI starting point the
// user can change.
function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  const fmt = (d) => d.toISOString().split('T')[0]; // YYYY-MM-DD
  return { from: fmt(from), to: fmt(to) };
}

const TABLE_OPTIONS = [
  {
    value: 'audit_log',
    label: 'Request log',
    description: 'Every authenticated write, login attempt, and sensitive read.',
  },
  {
    value: 'admin_audit_log',
    label: 'Admin action log',
    description: 'Privileged admin actions with detail of what changed.',
  },
];

export default function AuditExportModal({ isOpen, onClose }) {
  const initial = defaultRange();
  const [fromDate, setFromDate] = useState(initial.from);
  const [toDate, setToDate] = useState(initial.to);
  const [table, setTable] = useState('audit_log');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  async function handleExport() {
    setError(null);

    // Basic client-side validation before hitting the server.
    if (!fromDate || !toDate) {
      setError('Please choose both a start and end date.');
      return;
    }
    if (fromDate > toDate) {
      setError('Start date must be on or before end date.');
      return;
    }

    setExporting(true);
    try {
      const qs = new URLSearchParams({
        from: fromDate,
        to: toDate,
        table,
      }).toString();

      const res = await apiCall(`/audit/export?${qs}`);

      if (!res.ok) {
        // The backend returns a JSON {detail: "..."} body on 400/403.
        let message = `Export failed (status ${res.status}).`;
        try {
          const body = await res.json();
          if (body && body.detail) message = body.detail;
        } catch {
          // Non-JSON error body; keep the generic message.
        }
        setError(message);
        return;
      }

      // Success: response is a CSV blob. Trigger a browser download.
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `connex-audit-${table}-${fromDate}-to-${toDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      onClose();
    } catch (err) {
      console.error('Audit export failed:', err);
      setError('Something went wrong while exporting. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#2a2a2a]">
            <div>
              <h2 className="text-xl font-semibold text-white">Export Audit Log</h2>
              <p className="text-sm text-[#6a6a6a] mt-1">
                Download audit records as CSV for security review
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#2a2a2a] rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-[#a0a0a0]" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              {/* Table selector */}
              <div>
                <label className="text-xs text-[#6a6a6a] block mb-2">
                  What to export
                </label>
                <div className="space-y-2">
                  {TABLE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setTable(opt.value)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        table === opt.value
                          ? 'bg-blue-500/10 border-blue-500/40'
                          : 'bg-[#0a0a0a] border-[#2a2a2a] hover:border-[#3a3a3a]'
                      }`}
                    >
                      <p className="text-sm font-medium text-white">{opt.label}</p>
                      <p className="text-xs text-[#6a6a6a] mt-1">{opt.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Date range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#6a6a6a] block mb-2">From</label>
                  <input
                    type="date"
                    value={fromDate}
                    max={toDate || undefined}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full h-10 px-3 bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#6a6a6a] block mb-2">To</label>
                  <input
                    type="date"
                    value={toDate}
                    min={fromDate || undefined}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full h-10 px-3 bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-[#2a2a2a]">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleExport} disabled={exporting}>
              {exporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Export CSV
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}